import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import useAbortController from '@hooks/useAbortController';
import { useTranslations } from 'next-intl';
import { getHomeDeliverySlots, getPickUpInStoreSlots } from '@api/interfaces/cartApi';
import useCustomer from '@hooks/useCustomer';
import { trackDeliveryDate, trackDeliverySelectedTime } from '@helpers/analyticsHelpers/trackDeliveryWidget';
import paths from '@constants/paths';
import Config from '@config';
import Link from '@atoms/Link/Link';
import Spinner from '@molecules/Spinner/Spinner';
import TimeSlotDetails from '@molecules/TimeSlotDetails/TimeSlotDetails';
import TimeSlots from '@molecules/TimeSlots/TimeSlots';
import SlotAddressChanger from '@molecules/SlotAddressChanger/SlotAddressChanger';
import SlotDatePicker from '@molecules/SlotDatePicker/SlotDatePicker';
import logger from '@logger';
import { useRouter, usePathname } from 'next/navigation';
import { selectDeliveryPickerProceedToCheckout } from '@slices/deliveryPickerSlice';
import { dateToNumeric } from '@helpers/date';
import type {
  AxfoodCartViewModel,
  AxfoodStoreInfoViewModel,
  AxfoodSlotsScheduleViewModel,
  AxfoodTimeSlotViewModel,
} from '@occ/api-client';
import {
  StyledInfoContainer,
  StyledButtonContainer,
  StyledLoadingContainer,
  StyledSlotFooter,
  StyledSlotSelector,
} from './SlotSelector.styles';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import useCartActions from '@hooks/useCartActions';
import { getDeliveryAddress } from '@helpers/deliveryPickerHelper';
import DeliveryModeCode from '@enums/DeliveryModeCode';
import Text from '@atoms/Text/Text';
import Button from '@atoms/Button/Button';
import ScrollShadow from '@components/molecules/ScrollShadow/ScrollShadow';

interface Props {
  changeDeliveryHandler?: () => void;
  submitButtonText?: string;
  cancelButtonText?: string;
  onSubmitted?: () => void;
  onCancel?: () => void;
  deliveryMode?: 'homeDelivery' | 'pickUpInStore';
}

type SlotAddress = {
  line1: string;
  line2?: string;
};

const SlotSelector = ({
  onSubmitted,
  onCancel,
  submitButtonText,
  cancelButtonText,
  changeDeliveryHandler,
  deliveryMode,
}: Props) => {
  const dispatch = useAppDispatch();
  const { cart } = useCart();
  const { updateSlotInCart } = useCartActions();
  const proceedToCheckout = useAppSelector(selectDeliveryPickerProceedToCheckout);
  const { store } = useStore();
  const [slotData, setSlotData] = useState<AxfoodSlotsScheduleViewModel | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<AxfoodTimeSlotViewModel | null>(null);
  const [selectedDate, setSelectedDate] = useState<DayDate | null>(null);
  const [isPreselected, setIsPreselected] = useState(true);
  const [addressData, setAddressData] = useState<SlotAddress | null>(null);
  const [showExternalPickupLocationNotice, setShowExternalPickupLocationNotice] = useState(false);
  const { customer } = useCustomer();
  const t = useTranslations('slotSelector');
  const timeSlotsRef = useRef<HTMLDivElement | null>(null);

  const { getSignal } = useAbortController();
  const homeDelivery = useMemo(
    () => cart?.deliveryModeCode === Config.HOME_DELIVERY || false,
    [cart?.deliveryModeCode]
  );
  const pickUpInStore = useMemo(
    () => cart?.deliveryModeCode === Config.PICKUP_IN_STORE || false,
    [cart?.deliveryModeCode]
  );
  const disableChangeDeliveryMethod = useMemo(
    () => Boolean(cart?.orderReference) && cart?.franchiseStore,
    [cart?.franchiseStore, cart?.orderReference]
  );
  const router = useRouter();
  const pathname = usePathname();
  const isCheckout = useMemo(() => pathname === paths.CHECKOUT, [pathname]);
  const [waitForSubmit, setWaitForSubmit] = useState(false);

  const filteredSlots = slotData?.slots
    ? slotData?.slots?.filter((slot) => selectedDate?.groupKey === dateToNumeric(slot.startTime || 0))
    : [];

  const getSlots = useCallback(() => {
    if (homeDelivery) {
      return getHomeDeliverySlots(cart?.deliveryAddress?.postalCode as string, customer?.isB2BCustomer, getSignal());
    }
    return getPickUpInStoreSlots(store?.storeId || '', customer?.isB2BCustomer, getSignal());
  }, [cart?.deliveryAddress, customer?.isB2BCustomer, homeDelivery, store?.storeId, getSignal]);

  const buttonActive =
    selectedSlot && selectedSlot?.startTime && dateToNumeric(selectedSlot?.startTime) === selectedDate?.groupKey;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (homeDelivery || pickUpInStore) {
          setIsLoading(true);
          await getSlots()
            .then((r) => {
              const respData = r.data as AxfoodSlotsScheduleViewModel;
              setSlotData(respData);
              setSelectedSlot(respData.selectedSlot || null);
              setShowExternalPickupLocationNotice(respData.showExternalPickupLocationNotice);
              setIsLoading(false);
            })
            .catch((e) => {
              if (e instanceof Error) {
                if (e.name === 'AbortError' || e.name === 'CanceledError') return;
                logger.error({ error: 'Could not get slots', message: e.message });
              }
              setIsLoading(false);
            });
        }
      } catch (error) {
        if (error instanceof Error) {
          logger.error({ error: 'Failed to getSlots', message: error.message });
        }
      }
    };

    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const address = getDeliveryAddress({
      deliveryModeCode: cart?.deliveryModeCode as DeliveryModeCode,
      store: store as AxfoodStoreInfoViewModel,
      cart: cart as AxfoodCartViewModel,
    });
    if (!address && changeDeliveryHandler) changeDeliveryHandler();
    setAddressData(address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDate && !isPreselected) {
      trackDeliveryDate(homeDelivery, filteredSlots, isCheckout, store?.storeId || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    if (selectedSlot) {
      trackDeliverySelectedTime(homeDelivery, isCheckout, store?.storeId || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlot]);

  const onSubmit = useCallback(async () => {
    if (!selectedSlot) return;
    setWaitForSubmit(true);

    await updateSlotInCart(
      encodeURIComponent(selectedSlot.code),
      !!slotData?.tmsSlots,
      selectedSlot?.tmsDeliveryWindowReference,
      homeDelivery,
      isCheckout
    );

    setWaitForSubmit(false);
    if (onSubmitted) onSubmitted();
    if (proceedToCheckout && pathname?.startsWith(paths.CART)) {
      router.push(paths.CHECKOUT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, homeDelivery, isCheckout, onSubmitted, proceedToCheckout, pathname, selectedSlot, slotData?.tmsSlots]);

  const isExternalPickupLocation = useMemo(() => {
    return showExternalPickupLocationNotice && !store?.externalPickupLocation && !cart?.franchiseStore;
  }, [showExternalPickupLocationNotice, store?.externalPickupLocation, cart?.franchiseStore]);

  return (
    <StyledSlotSelector>
      <SlotAddressChanger
        changeDeliveryHandler={changeDeliveryHandler}
        address={addressData}
        disableChangeDeliveryMethod={disableChangeDeliveryMethod}
      />
      {isLoading ? (
        <StyledLoadingContainer>
          <Text type="subhead" size="small">
            {t('loading')}
          </Text>
          <Spinner size={32} />
        </StyledLoadingContainer>
      ) : (
        <>
          {isExternalPickupLocation && (
            <StyledInfoContainer data-testid="external-notice">
              <Text align="center" type="body" size="small">
                {t('externalNotice')}
              </Text>
              <Text align="center" type="body" size="small">
                <Link href={paths.PICKUP_STORES} target="_blank" rel="noreferrer">
                  {t('showAllPickupLocations')}
                </Link>
              </Text>
            </StyledInfoContainer>
          )}
          {cart?.franchiseStore && (
            <StyledInfoContainer data-testid="franchise-notice">
              <Text type="body" size="small" align="center">
                {t('franchiseNotice')}
              </Text>
            </StyledInfoContainer>
          )}
          {slotData && (
            <SlotDatePicker
              slotData={slotData}
              daysAhead={3}
              onSelectDate={(date, preselected) => {
                setSelectedDate(date);
                setIsPreselected(preselected);

                if (timeSlotsRef.current) {
                  setTimeout(() => {
                    const buttonElement =
                      timeSlotsRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])');
                    buttonElement?.focus();
                  }, 0);
                }
              }}
              selectedDate={selectedDate}
            />
          )}

          <ScrollShadow>
            <TimeSlots
              deliveryMode={deliveryMode}
              slots={filteredSlots}
              onSlotClick={setSelectedSlot}
              selectedSlot={selectedSlot}
              ref={timeSlotsRef}
            />
          </ScrollShadow>
          {buttonActive && (
            <StyledSlotFooter>
              {selectedSlot && <TimeSlotDetails slot={selectedSlot} />}
              <StyledButtonContainer>
                {cancelButtonText && onCancel && (
                  <Button fullWidth variant="secondary" onClick={onCancel} disabled={!buttonActive}>
                    {cancelButtonText}
                  </Button>
                )}
                <Button
                  fullWidth
                  onClick={onSubmit}
                  data-testid="timeslot-submit"
                  disabled={!buttonActive}
                  isSubmitting={waitForSubmit}
                >
                  {submitButtonText || t('confirmSlot')}
                </Button>
              </StyledButtonContainer>
            </StyledSlotFooter>
          )}
        </>
      )}
    </StyledSlotSelector>
  );
};

export default SlotSelector;
